import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  MenuItem,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Switch,
  TextField,
  Typography,
  CircularProgress,
  Paper,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import React, { useState, useEffect } from "react";
import { callBackendApi } from "../../methods/axiosInstances/backendInstance/apiRequest";
import { useDispatch } from "react-redux";
import { showSnackbar } from "../../store/snackbarSlice/snackbarSlice";
import { useParams, useNavigate } from "react-router-dom";
import { getUserById } from "../../methods/user/userRequests";
import { getError } from "../../helpers/form/errorHelper";
import ROLES from "../../enums/Roles";

const formatLabel = (text) => {
  if (!text) return "";
  return text
    .replace(/_/g, " ")
    .replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1));
};

const normalizeRows = (response) => {
  if (!response?.success) return [];
  if (Array.isArray(response.data)) return response.data;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  return [];
};

const toDisplay = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") {
    if (value.name) return value.name;
    if (value.id) return value.id;
    return JSON.stringify(value);
  }
  return String(value);
};

const EmployeeProfile = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [lookups, setLookups] = useState({});
  const [errors, setErrors] = useState({});
  const [relatedLoading, setRelatedLoading] = useState(false);
  const [relatedError, setRelatedError] = useState("");
  const [relatedData, setRelatedData] = useState({
    classes: {},
    inventory: {},
    assets: {},
    catalog: {},
    sales: {},
    access: {},
  });

  const [user, setUser] = useState({
    name: "",
    email: "",
    role: "EMPLOYEE",
    password: "",
    is_active: 1,
    employee_info: {
      preferred_name: "",
      initials: "",
      birth_date: "",
      nif: "",
      document_number: "",
      document_expiry: "",
      start_date: "",
      end_date: "",
      title_id: "",
      job_position_id: "",
      gender_id: "",
      civil_status_id: "",
      document_type_id: "",
      nationality_id: "",
      department_id: "",
      role_id: "",
      notes: "",
      receive_sms: 0,
      receive_email: 0,
      marketing: 0,
      iban: "",
    },
    contact: {
      address: "",
      city: "",
      postal_code: "",
      country_id: "",
      phone: "",
      mobile: "",
      email: "",
      emergency_contact: "",
    },
    marketing_info: {
      receive_sms: 0,
      receive_email: 0,
      allow_marketing: 0,
      image_consent: 0,
      show_in_program: 0,
      show_in_app: 0,
    },
    medical_info: {
      health_number: "",
      insurance: 0,
      notes: "",
      has_medical_issues: false,
      healthcare_system: "",
    },
  });

  const UserRoleOptions = [
    {
      id: ROLES.ADMIN,
      name: ROLES.ADMIN,
    },
    {
      id: ROLES.EMPLOYEE,
      name: ROLES.EMPLOYEE,
    },
  ];

  // helper para ler o value correto mesmo em nested (contact, etc)
  const getFieldValue = (section, field) => {
    if (section === "root") {
      return user?.[field] ?? "";
    }

    let obj = user;
    const parts = section.split(".");
    for (const p of parts) {
      if (!obj || typeof obj !== "object") return "";
      obj = obj[p];
    }

    return obj?.[field] ?? "";
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const [lookupsRes, userRes] = await Promise.all([
          callBackendApi("/lookups", "GET"),
          id ? getUserById(id) : Promise.resolve({ success: false }),
        ]);

        if (lookupsRes.success) setLookups(lookupsRes.data.data);

        if (id && userRes.success) {
          const normalized = {
            ...userRes.data,
            employee_info: {
              ...(userRes.data.employee_info || {}),
            },
            contact: {
              ...(userRes.data.contact || {
                address: "",
                city: "",
                postal_code: "",
                country_id: "",
                phone: "",
                mobile: "",
                email: "",
                emergency_contact: "",
              }),
            },
            marketing_info: {
              ...(userRes.data.marketing_info || {
                receive_sms: 0,
                receive_email: 0,
                allow_marketing: 0,
                image_consent: 0,
                show_in_program: 0,
                show_in_app: 0,
              }),
            },
            medical_info: {
              ...(userRes.data.medical_info || {
                health_number: "",
                insurance: 0,
                notes: "",
                has_medical_issues: false,
                healthcare_system: "",
              }),
            },
          };
          setUser(normalized);
        }
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id]);

  useEffect(() => {
    const loadRelatedData = async () => {
      if (!id) return;

      setRelatedLoading(true);
      setRelatedError("");

      try {
        const [
          gymClassesRes,
          classSessionsRes,
          classBookingsRes,
          stockMovementsRes,
          maintenanceLogsRes,
          incidentsRes,
          saleRes,
          saleLinesRes,
          salePaymentsRes,
          saleRefundsRes,
          memberEntitlementsRes,
          entitlementMovementsRes,
        ] = await Promise.all([
          callBackendApi("/gym-classes", "GET"),
          callBackendApi("/class-sessions", "GET"),
          callBackendApi("/class-bookings", "GET"),
          callBackendApi("/inventory-stock-movements", "GET"),
          callBackendApi("/asset-maintenance-logs", "GET"),
          callBackendApi("/asset-incidents", "GET"),
          callBackendApi("/sales", "GET"),
          callBackendApi("/sale-lines", "GET"),
          callBackendApi("/sale-payments", "GET"),
          callBackendApi("/sale-refunds", "GET"),
          callBackendApi("/member-entitlements", "GET"),
          callBackendApi("/entitlement-movements", "GET"),
        ]);

        const gymClasses = normalizeRows(gymClassesRes).filter(
          (row) => String(row.professor_id || "") === String(id)
        );
        const classSessions = normalizeRows(classSessionsRes).filter(
          (row) => String(row.professor_id || "") === String(id)
        );

        const classSessionIds = new Set(classSessions.map((session) => session.id));
        const classBookings = normalizeRows(classBookingsRes).filter((row) =>
          classSessionIds.has(row.class_session_id)
        );

        const inventoryMovements = normalizeRows(stockMovementsRes).filter(
          (row) => String(row.created_by_user_id || "") === String(id)
        );

        const maintenanceLogs = normalizeRows(maintenanceLogsRes).filter(
          (row) => String(row.created_by_user_id || "") === String(id)
        );

        const incidents = normalizeRows(incidentsRes).filter(
          (row) => String(row.reported_by_user_id || "") === String(id)
        );

        const sales = normalizeRows(saleRes).filter(
          (row) => String(row.sold_by_user_id || "") === String(id)
        );
        const saleIds = new Set(sales.map((sale) => sale.id));
        const saleLines = normalizeRows(saleLinesRes).filter((row) =>
          saleIds.has(row.sale_id)
        );
        const salePayments = normalizeRows(salePaymentsRes).filter((row) =>
          saleIds.has(row.sale_id)
        );
        const saleRefunds = normalizeRows(saleRefundsRes).filter(
          (row) =>
            saleIds.has(row.sale_id) || String(row.refunded_by_user_id || "") === String(id)
        );

        const memberEntitlements = normalizeRows(memberEntitlementsRes).filter(
          (row) => String(row.user_id || "") === String(id)
        );
        const entitlementIds = new Set(memberEntitlements.map((entitlement) => entitlement.id));
        const entitlementMovements = normalizeRows(entitlementMovementsRes).filter((row) =>
          entitlementIds.has(row.entitlement_id)
        );

        setRelatedData({
          classes: {
            gym_classes: gymClasses,
            class_sessions: classSessions,
            class_bookings: classBookings,
          },
          inventory: {
            stock_movements: inventoryMovements,
          },
          assets: {
            maintenance_logs: maintenanceLogs,
            incidents,
          },
          catalog: {
            sold_products: saleLines,
          },
          sales: {
            sales,
            sale_lines: saleLines,
            sale_payments: salePayments,
            sale_refunds: saleRefunds,
          },
          access: {
            member_entitlements: memberEntitlements,
            entitlement_movements: entitlementMovements,
          },
        });
      } catch (error) {
        setRelatedError("Failed to load related information.");
      } finally {
        setRelatedLoading(false);
      }
    };

    loadRelatedData();
  }, [id]);

  const handleChange = (section, field, value) => {
    setUser((prev) => {
      const updated = { ...prev };
      if (section === "root") {
        updated[field] = value;
      } else if (section.includes(".")) {
        const [main, sub] = section.split(".");
        updated[main] = {
          ...updated[main],
          [sub]: { ...updated[main]?.[sub], [field]: value },
        };
      } else {
        updated[section] = { ...updated[section], [field]: value };
      }
      return updated;
    });
  };

  const handleSubmit = async () => {
    setSaving(true);
    setErrors({});

    try {
      const method = id ? "PUT" : "POST";
      const url = id ? `/users/${id}` : "/users";
      const res = await callBackendApi(url, method, user);

      if (res.success) {
        dispatch(
          showSnackbar({
            message: id
              ? "User updated successfully!"
              : "User created successfully!",
          })
        );
      } else {
        setErrors(res?.error || {});
        dispatch(showSnackbar({ message: "Error saving user." }));
      }
    } catch (error) {
      console.error(error);
      dispatch(showSnackbar({ message: "Unexpected error while saving." }));
    } finally {
      setSaving(false);
    }
  };

  const renderSelect = (label, section, field, list = []) => {
    const errorPath = section === "root" ? field : `${section}.${field}`;

    return (
      <TextField
        select
        fullWidth
        label={formatLabel(label || field)}
        value={String(getFieldValue(section, field) ?? "")}
        onChange={(e) =>
          handleChange(section, field, e.target.value ? e.target.value : "")
        }
        helperText={getError(errors, errorPath)}
        error={!!getError(errors, errorPath)}
      >
        <MenuItem value="">-- Select --</MenuItem>
        {list.map((item) => (
          <MenuItem key={item.id} value={String(item.id)}>
            {item.name}
          </MenuItem>
        ))}
      </TextField>
    );
  };

  const renderSwitch = (label, section, field, checked) => (
    <FormControlLabel
      control={
        <Switch
          checked={!!checked}
          onChange={(e) =>
            handleChange(section, field, e.target.checked ? 1 : 0)
          }
        />
      }
      label={label}
    />
  );

  const renderRelationTable = (title, rows, columns) => (
    <Accordion key={title} disableGutters sx={{ mb: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle2">
          {title} ({rows.length})
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No records found.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell key={`${title}-${col}`}>{formatLabel(col)}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow key={`${title}-${row.id || index}`}>
                  {columns.map((col) => (
                    <TableCell key={`${title}-${row.id || index}-${col}`}>
                      {toDisplay(row[col])}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </AccordionDetails>
    </Accordion>
  );

  const renderRelatedTab = () => (
    <Card>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Related Information
        </Typography>
        {relatedError ? <Alert severity="warning" sx={{ mb: 2 }}>{relatedError}</Alert> : null}
        {relatedLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant="subtitle1" sx={{ mt: 1, mb: 1 }}>Classes</Typography>
            {renderRelationTable("Gym Classes", relatedData.classes.gym_classes || [], [
              "name",
              "status",
              "capacity",
              "created_at",
            ])}
            {renderRelationTable("Class Sessions", relatedData.classes.class_sessions || [], [
              "class_id",
              "starts_at",
              "ends_at",
              "capacity",
              "status",
            ])}
            {renderRelationTable("Class Bookings", relatedData.classes.class_bookings || [], [
              "class_session_id",
              "user_id",
              "status",
              "booked_at",
            ])}

            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Inventory</Typography>
            {renderRelationTable("Stock Movements", relatedData.inventory.stock_movements || [], [
              "movement_type",
              "qty",
              "unit_cost",
              "reason",
              "created_at",
            ])}

            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Assets</Typography>
            {renderRelationTable("Maintenance Logs", relatedData.assets.maintenance_logs || [], [
              "asset_id",
              "log_type",
              "occurred_at",
              "cost",
            ])}
            {renderRelationTable("Asset Incidents", relatedData.assets.incidents || [], [
              "asset_id",
              "status",
              "severity",
              "reported_at",
            ])}

            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Catalog</Typography>
            {renderRelationTable("Sold Products (Sale Lines)", relatedData.catalog.sold_products || [], [
              "sale_id",
              "product_id",
              "product_name_snapshot",
              "qty",
              "unit_price",
            ])}

            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Sales</Typography>
            {renderRelationTable("Sales", relatedData.sales.sales || [], [
              "user_id",
              "channel",
              "status",
              "total",
              "occurred_at",
            ])}
            {renderRelationTable("Sale Payments", relatedData.sales.sale_payments || [], [
              "sale_id",
              "method",
              "amount",
              "paid_at",
            ])}
            {renderRelationTable("Sale Refunds", relatedData.sales.sale_refunds || [], [
              "sale_id",
              "status",
              "total_refunded",
              "occurred_at",
            ])}

            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Access</Typography>
            {renderRelationTable("Member Entitlements", relatedData.access.member_entitlements || [], [
              "user_id",
              "product_id",
              "status",
              "starts_at",
              "ends_at",
            ])}
            {renderRelationTable("Entitlement Movements", relatedData.access.entitlement_movements || [], [
              "entitlement_id",
              "movement_type",
              "quantity",
              "reference_type",
              "reference_id",
            ])}
          </>
        )}
      </CardContent>
    </Card>
  );

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );

  const emp = user.employee_info;
  const contact = user.contact;
  const marketing = user.marketing_info;
  const medical = user.medical_info;

  return (
    <Box sx={{ width: "100%", maxWidth: 1400, mx: "auto", mt: 4 }}>
      <Card className="my-5">
        <CardContent>
          <Grid container>
            <Grid item xs={1}>
              <IconButton onClick={() => window.history.back()} color="primary">
                <ArrowBackIcon />
              </IconButton>
            </Grid>
            <Grid item xs={11}>
              <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                {id ? "Edit User" : "Create User"}{" "}
                {user.name ? ` - ${user.name}` : ""}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      {id ? (
        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ pb: "8px !important" }}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
              <Button variant="outlined" onClick={() => navigate(`/dashboard/users/employee/${id}/agenda`)}>
                Open Agenda
              </Button>
            </Box>
            <Tabs value={activeTab} onChange={(_, next) => setActiveTab(next)}>
              <Tab label="Profile" />
              <Tab label="Related Information" />
            </Tabs>
          </CardContent>
        </Card>
      ) : null}

      {activeTab === 1 ? renderRelatedTab() : (
      <Card>
        <CardContent>
          {/* Account Section */}
          <Typography variant="h6" sx={{ mb: 2 }}>
            Personal Data
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              {renderSelect(
                "Title",
                "employee_info",
                "title_id",
                lookups.titles || []
              )}
            </Grid>
            <Grid xs={4}></Grid>
            <Grid item xs={6}>
              <TextField
                label={formatLabel("preferred_name")}
                fullWidth
                value={emp["preferred_name"] || ""}
                onChange={(e) =>
                  handleChange(
                    "employee_info",
                    "preferred_name",
                    e.target.value
                  )
                }
                helperText={getError(errors, `employee_info.preferred_name`)}
                error={!!getError(errors, `employee_info.preferred_name`)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Name"
                fullWidth
                value={user.name}
                onChange={(e) => handleChange("root", "name", e.target.value)}
                helperText={getError(errors, "name")}
                error={!!getError(errors, "name")}
              />
            </Grid>
            <Grid item xs={6}>
              {renderSelect(
                "Gender",
                "employee_info",
                "gender_id",
                lookups.genders || []
              )}
            </Grid>
            <Grid item xs={6}>
              <TextField
                label={formatLabel("birth_date")}
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={emp["birth_date"] || ""}
                onChange={(e) =>
                  handleChange("employee_info", "birth_date", e.target.value)
                }
                helperText={getError(errors, `employee_info.birth_date`)}
                error={!!getError(errors, `employee_info.birth_date`)}
              />
            </Grid>
            <Grid item xs={4}>
              {renderSelect(
                "Document Type",
                "employee_info",
                "document_type_id",
                lookups.document_types || []
              )}
            </Grid>
            <Grid item xs={4}>
              <TextField
                label={formatLabel("document_number")}
                fullWidth
                value={emp["document_number"] || ""}
                onChange={(e) =>
                  handleChange(
                    "employee_info",
                    "document_number",
                    e.target.value
                  )
                }
                helperText={getError(errors, `employee_info.document_number`)}
                error={!!getError(errors, `employee_info.document_number`)}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label={formatLabel("document_expiry")}
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={emp["document_expiry"] || ""}
                onChange={(e) =>
                  handleChange(
                    "employee_info",
                    "document_expiry",
                    e.target.value
                  )
                }
                helperText={getError(errors, `employee_info.document_expiry`)}
                error={!!getError(errors, `employee_info.document_expiry`)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={formatLabel("iban")}
                fullWidth
                value={emp["iban"] || ""}
                onChange={(e) =>
                  handleChange(
                    "employee_info",
                    "iban",
                    e.target.value
                  )
                }
                helperText={getError(errors, `employee_info.iban`)}
                error={!!getError(errors, `employee_info.iban`)}
              />
            </Grid>
            <Grid item xs={4}>
              {renderSelect(
                "Nationality",
                "employee_info",
                "nationality_id",
                lookups.countries || []
              )}
            </Grid>

            <Grid item xs={4}>
              {renderSelect(
                "Country",
                "contact",
                "country_id",
                lookups.countries || []
              )}
            </Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />

          {/* Employee Section - only if EMPLOYEE */}
          <>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Employee Info
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                {renderSelect("Role", "root", "role", UserRoleOptions)}
              </Grid>
              <Grid item xs={4}>
                <Grid item xs={4} />
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!user.is_active}
                      onChange={(e) =>
                        handleChange(
                          "root",
                          "is_active",
                          e.target.checked ? 1 : 0
                        )
                      }
                    />
                  }
                  label="Active Employee"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Email"
                  fullWidth
                  value={user.email}
                  onChange={(e) =>
                    handleChange("root", "email", e.target.value)
                  }
                  helperText={getError(errors, "email")}
                  error={!!getError(errors, "email")}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Password"
                  fullWidth
                  type="password"
                  value={user.password}
                  onChange={(e) =>
                    handleChange("root", "password", e.target.value)
                  }
                  helperText={getError(errors, "password")}
                  error={!!getError(errors, "password")}
                />
              </Grid>
              <Grid item xs={4}>
                {renderSelect(
                  "Department",
                  "employee_info",
                  "department_id",
                  lookups.departments || []
                )}
              </Grid>
              <Grid item xs={4}>
                {renderSelect(
                  "Job Position",
                  "employee_info",
                  "job_position_id",
                  lookups.job_positions || []
                )}
              </Grid>
              {["nif"].map((f) => (
                <Grid item xs={4} key={f}>
                  <TextField
                    label={formatLabel(f)}
                    fullWidth
                    value={emp[f] || ""}
                    onChange={(e) =>
                      handleChange("employee_info", f, e.target.value)
                    }
                    helperText={getError(errors, `employee_info.${f}`)}
                    error={!!getError(errors, `employee_info.${f}`)}
                  />
                </Grid>
              ))}

              {["start_date", "end_date"].map((f) => (
                <Grid item xs={3} key={f}>
                  <TextField
                    label={formatLabel(f)}
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={emp[f] || ""}
                    onChange={(e) =>
                      handleChange("employee_info", f, e.target.value)
                    }
                    helperText={getError(errors, `employee_info.${f}`)}
                    error={!!getError(errors, `employee_info.${f}`)}
                  />
                </Grid>
              ))}
              <Grid item xs={12}>
                <TextField
                  label="Employee Notes"
                  fullWidth
                  multiline
                  rows={2}
                  value={emp.notes || ""}
                  onChange={(e) =>
                    handleChange("employee_info", "notes", e.target.value)
                  }
                  helperText={getError(errors, "employee_info.notes")}
                  error={!!getError(errors, "employee_info.notes")}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            {/* Contact Section */}
            <Typography variant="h6" sx={{ mb: 2 }}>
              Contact Info
            </Typography>
            <Grid container spacing={2}>
              {[
                "address",
                "city",
                "postal_code",
                "phone",
                "mobile",
                "emergency_contact",
              ].map((f) => (
                <Grid item xs={4} key={f}>
                  <TextField
                    label={formatLabel(f)}
                    fullWidth
                    value={contact[f] || ""}
                    onChange={(e) => handleChange("contact", f, e.target.value)}
                    helperText={getError(errors, `contact.${f}`)}
                    error={!!getError(errors, `contact.${f}`)}
                  />
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ my: 4 }} />
          </>

          {/* Medical Section */}
          <Typography variant="h6" sx={{ mb: 2 }}>
            Medical Info
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField
                label="Health Number"
                fullWidth
                value={medical.health_number || ""}
                onChange={(e) =>
                  handleChange("medical_info", "health_number", e.target.value)
                }
                helperText={getError(errors, "medical_info.health_number")}
                error={!!getError(errors, "medical_info.health_number")}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Healthcare System"
                fullWidth
                value={medical.healthcare_system || ""}
                onChange={(e) =>
                  handleChange(
                    "medical_info",
                    "healthcare_system",
                    e.target.value
                  )
                }
                helperText={getError(errors, "medical_info.healthcare_system")}
                error={!!getError(errors, "medical_info.healthcare_system")}
              />
            </Grid>
            <Grid item xs={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!medical.insurance}
                    onChange={(e) =>
                      handleChange(
                        "medical_info",
                        "insurance",
                        e.target.checked ? 1 : 0
                      )
                    }
                  />
                }
                label="Has Insurance"
              />
            </Grid>
            <Grid item xs={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!medical.has_medical_issues}
                    onChange={(e) =>
                      handleChange(
                        "medical_info",
                        "has_medical_issues",
                        e.target.checked ? 1 : 0
                      )
                    }
                  />
                }
                label="Has Medical Issues"
              />
            </Grid>
            <Grid item xs={8}>
              <TextField
                label="Medical Notes"
                fullWidth
                multiline
                rows={2}
                value={medical.notes || ""}
                onChange={(e) =>
                  handleChange("medical_info", "notes", e.target.value)
                }
                helperText={getError(errors, "medical_info.notes")}
                error={!!getError(errors, "medical_info.notes")}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />

          {/* Marketing Section */}
          <Typography variant="h6" sx={{ mb: 2 }}>
            Marketing Preferences
          </Typography>
          <Grid container spacing={2}>
            {Object.entries({
              receive_sms: "Receive SMS",
              receive_email: "Receive Email",
              allow_marketing: "Allow Marketing",
              image_consent: "Image Consent",
              show_in_program: "Show In Program",
              show_in_app: "Show In App",
            }).map(([f, label]) => (
              <Grid item xs={3} key={f}>
                {renderSwitch(label, "marketing_info", f, marketing[f])}
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 4 }} />

          <Box sx={{ textAlign: "right" }}>
            <Button
              type="button"
              fullWidth={false}
              variant="contained"
              onClick={handleSubmit}
              disabled={saving}
            >
              {saving ? "Saving..." : id ? "💾 Save Changes" : "➕ Create User"}
            </Button>
          </Box>
        </CardContent>
      </Card>
      )}
    </Box>
  );
};

export default EmployeeProfile;
